import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';

const PhotoGallery = () => {
  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [viewMode, setViewMode] = useState('before'); // 'before' or 'after'

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchGallery();
  }, []);

  const fetchGallery = async () => {
    try {
      const response = await fetch(`${API_URL}/api/photos/gallery`);
      if (response.ok) {
        const data = await response.json();
        setGalleryItems(data);
      }
    } catch (error) {
      console.error('Error fetching gallery:', error);
    } finally {
      setLoading(false);
    }
  };

  const openLightbox = (item) => {
    setSelectedItem(item);
    setViewMode('before');
  };

  const closeLightbox = () => {
    setSelectedItem(null);
  };

  if (loading || galleryItems.length === 0) {
    return null; // Don't show gallery if loading or no photos
  }

  return (
    <>
      <section id="photo-gallery" className="py-24 bg-zinc-950">
        <div className="container mx-auto px-4">
          {/* Section Header */}
          <div className="text-center mb-16">
            <div className="inline-block bg-red-600/20 border border-red-500/50 rounded-full px-4 py-2 mb-4">
              <span className="text-red-500 font-semibold text-sm">Our Work</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Before & After Gallery
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              See the transformation. Real results from our detail services.
            </p>
          </div>

          {/* Gallery Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {galleryItems.map((item) => {
              const beforePhoto = item.before_photos[0];
              const afterPhoto = item.after_photos[0];

              if (!beforePhoto && !afterPhoto) return null;

              return (
                <div
                  key={item.booking_id}
                  className="group relative overflow-hidden rounded-lg border border-zinc-800 hover:border-red-500 transition-all cursor-pointer"
                  onClick={() => openLightbox(item)}
                >
                  {/* Before/After Split View */}
                  <div className="relative h-64">
                    {beforePhoto && (
                      <div className="absolute inset-0 w-1/2 left-0">
                        <img
                          src={beforePhoto.cloudinary_url}
                          alt="Before"
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute top-2 left-2 bg-red-600 text-white text-xs font-bold px-2 py-1 rounded">
                          BEFORE
                        </div>
                      </div>
                    )}
                    {afterPhoto && (
                      <div className="absolute inset-0 w-1/2 right-0">
                        <img
                          src={afterPhoto.cloudinary_url}
                          alt="After"
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute top-2 right-2 bg-green-600 text-white text-xs font-bold px-2 py-1 rounded">
                          AFTER
                        </div>
                      </div>
                    )}
                    <div className="absolute inset-y-0 left-1/2 w-0.5 bg-white z-10"></div>
                  </div>

                  {/* Hover Overlay */}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <span className="text-white font-semibold">Click to view</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {selectedItem && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4">
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white hover:text-red-500 transition-colors z-10"
          >
            <X className="w-8 h-8" />
          </button>

          <div className="max-w-6xl w-full">
            {/* Before/After Toggle */}
            <div className="flex justify-center space-x-4 mb-6">
              <button
                onClick={() => setViewMode('before')}
                className={`px-6 py-2 rounded-lg font-semibold transition-colors ${
                  viewMode === 'before'
                    ? 'bg-red-600 text-white'
                    : 'bg-zinc-800 text-gray-400 hover:text-white'
                }`}
              >
                Before ({selectedItem.before_photos.length})
              </button>
              <button
                onClick={() => setViewMode('after')}
                className={`px-6 py-2 rounded-lg font-semibold transition-colors ${
                  viewMode === 'after'
                    ? 'bg-green-600 text-white'
                    : 'bg-zinc-800 text-gray-400 hover:text-white'
                }`}
              >
                After ({selectedItem.after_photos.length})
              </button>
            </div>

            {/* Images */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[70vh] overflow-y-auto p-2">
              {viewMode === 'before' &&
                selectedItem.before_photos.map((photo) => (
                  <img
                    key={photo.id}
                    src={photo.cloudinary_url}
                    alt="Before"
                    className="w-full h-64 object-cover rounded-lg"
                  />
                ))}
              {viewMode === 'after' &&
                selectedItem.after_photos.map((photo) => (
                  <img
                    key={photo.id}
                    src={photo.cloudinary_url}
                    alt="After"
                    className="w-full h-64 object-cover rounded-lg"
                  />
                ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PhotoGallery;
